import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route } from 'react-router-dom';
import { ForgeProvider, ForgeFloatingButton } from '@design2code/forge-ui';
import { useAppDispatch, useAppSelector } from './app/hooks';
import { fetchCurrentUser } from './app/slices/authSlice';
import ProtectedRoute from './components/common/ProtectedRoute';
import { MainLayout } from './components/layout/MainLayout';
import { PageLoader } from './components/common/PageLoader';

// =============================================================================
// MODULAR ROUTE GROUPS (DEC-008 P2 - Route-based Code Splitting)
// =============================================================================
import TradingRoutes from './routes/tradingRoutes';
import OmsRoutes from './routes/omsRoutes';
import AnalyticsRoutes from './routes/analyticsRoutes';
import DataRoutes from './routes/dataRoutes';
import ComplianceRoutes from './routes/complianceRoutes';
import RiskRoutes from './routes/riskRoutes';
import OperationsRoutes from './routes/operationsRoutes';
import MultiBookRoutes from './routes/multiBookRoutes';
import CollateralRoutes from './routes/collateralRoutes';
import FofRoutes from './routes/fofRoutes';
import ReportRoutes from './routes/reportRoutes';
import SettingsRoutes from './routes/settingsRoutes';
import MiscRoutes from './routes/miscRoutes';
import MarketDataRoutes from './routes/marketDataRoutes';
import TradingWorkbenchRoutes from './routes/tradingWorkbenchRoutes';
import AdminRoutes from './routes/adminRoutes';

// =============================================================================
// Pages kept in App.tsx (auth + catch-all only)
// =============================================================================
const LoginPage = lazy(() => import('./pages/LoginPage'));
const PlaceholderPage = lazy(() => import('./pages/PlaceholderPage'));

// Chief orchestrator console — sourced from qiyas_mirror-chief-service via the
// @chief vite alias (agentic layer, served by chief on :8300)
const OrchestratorPage = lazy(() => import('@chief/components/orchestrator/OrchestratorPage'));

// FORGE requirement capture (Cmd+K widget + admin review dashboard)
const ForgeAdminPage = lazy(() => import('./modules/forge/ForgeAdminPage'));

// =============================================================================
// Helper: Wrap page with Suspense
// =============================================================================
function LazyPage({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function App() {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);

  // On mount, try to restore session from localStorage bearer token via /auth/me
  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(fetchCurrentUser());
    }
  }, [isAuthenticated, dispatch]);

  // Mirror portal session keys for the chief orchestrator console — @chief
  // services read 'access_token'/'tenant_id', the portal stores 'qiyas_*'.
  // Covers sessions that logged in before the mirror existed in platformClient.
  useEffect(() => {
    const token = localStorage.getItem('qiyas_access_token');
    if (token) localStorage.setItem('access_token', token);
    const tenant = localStorage.getItem('qiyas_tenant_id');
    if (tenant) localStorage.setItem('tenant_id', tenant);
  }, [isAuthenticated]);

  return (
    <ForgeProvider serviceId="qiyas" forgeApiUrl="/api/v1/forge">
    <Routes>
      {/* Authentication */}
      <Route path="/auth" element={<LazyPage><LoginPage /></LazyPage>} />
      {/* /login is a compat alias for /auth: 401 interceptors + external bookmarks
          historically target /login, which had no route and fell through to the
          catch-all "Route Not Found" (2026-07-15 bug). Render the real login here. */}
      <Route path="/login" element={<LazyPage><LoginPage /></LazyPage>} />

      {/* Modular Route Groups — called as functions to return Route fragments */}
      {TradingRoutes()}
      {TradingWorkbenchRoutes()}
      {OmsRoutes()}
      {AnalyticsRoutes()}
      {DataRoutes()}
      {MarketDataRoutes()}
      {ComplianceRoutes()}
      {RiskRoutes()}
      {OperationsRoutes()}
      {MultiBookRoutes()}
      {CollateralRoutes()}
      {FofRoutes()}
      {ReportRoutes()}
      {SettingsRoutes()}
      {MiscRoutes()}
      {AdminRoutes()}

      {/* Chief orchestrator console */}
      <Route path="/chief/orchestrator" element={<ProtectedRoute><MainLayout><LazyPage><OrchestratorPage /></LazyPage></MainLayout></ProtectedRoute>} />

      {/* FORGE admin review dashboard */}
      <Route path="/forge/admin" element={<ProtectedRoute><MainLayout><LazyPage><ForgeAdminPage /></LazyPage></MainLayout></ProtectedRoute>} />

      {/* Catch-all */}
      <Route path="*" element={<ProtectedRoute><MainLayout><LazyPage><PlaceholderPage /></LazyPage></MainLayout></ProtectedRoute>} />
    </Routes>
    {isAuthenticated && <ForgeFloatingButton position="bottom-right" serviceId="qiyas" />}
    </ForgeProvider>
  );
}

export default App;
